import { z } from 'zod';

/**
 * A form's shape is defined once, here, and reused by the resolver and by anything that
 * parses the corresponding API payload — so a backend contract change surfaces as a type
 * error rather than a runtime `undefined`.
 */

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Enter your current password.'),
    new_password: z
      .string()
      .min(12, 'Use at least 12 characters.')
      .regex(/[a-z]/, 'Include a lowercase letter.')
      .regex(/[A-Z]/, 'Include an uppercase letter.')
      .regex(/\d/, 'Include a digit.'),
    confirm_password: z.string(),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    message: 'The passwords do not match.',
    path: ['confirm_password'],
  });

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const profileSchema = z.object({
  first_name: z.string().max(150).optional(),
  last_name: z.string().max(150).optional(),
  phone: z.string().max(32).optional(),
  locale: z.string().max(16).optional(),
  timezone: z.string().max(64).optional(),
});

export type ProfileInput = z.infer<typeof profileSchema>;
